import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import styles from "./Contractors.module.css";
import { pliStatusBadge } from "../services/pliService";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const Contractors = () => {
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);

  // State for sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Ref to maintain search input focus
  const searchInputRef = useRef(null);

  // Column mapping for server-side sorting
  const getSortColumnIndex = (field) => {
    const mapping = {
      "contractor_id": 0,  // contractor.contractor_id
      "name": 1,           // contractor.name
      "email": 2,          // contractor.email
      "phone_number": 3,   // contractor.phone_number
      "town": 4,           // contractor.town
      "postcode": 5,       // contractor.postcode
      "reviewDate": 6,     // contractor.created_at
    };
    return mapping[field] || 1;
  };

  // Track if search input is focused and cursor position
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const cursorPositionRef = useRef(0);

  // Debounce search input with shorter delay for real-time feel
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300); // Reduced to 300ms for more responsive search

    return () => clearTimeout(timer);
  }, [search]);

  // Maintain focus on search input after re-renders
  useEffect(() => {
    if (isSearchFocused && searchInputRef.current) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position
          const cursorPos = cursorPositionRef.current || search.length;
          try {
            searchInputRef.current.setSelectionRange(cursorPos, cursorPos);
          } catch (e) {
            // Some inputs don't support setSelectionRange
            console.log("Could not set cursor position:", e);
          }
        }
      });
    }
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const loadContractors = useCallback(async () => {
    // Don't show loading spinner if user is typing (to prevent focus loss)
    if (!isSearchFocused) {
      setLoading(true);
    }

    const datatableParams = {
      draw: draw,
      start: (currentPage - 1) * pageSize,
      length: pageSize,
      search: { value: debouncedSearch },
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
    };

    console.log("📊 [FRONTEND] Fetching contractors with params:", datatableParams);

    try {
      // Backend route: /api/contractors/datatable
      const response = await api.post("/contractors/datatable", datatableParams);
      const data = response.data;
      console.log("✅ [FRONTEND] Received contractors data:", data);

      setContractors(data.data || []);
      setTotalRecords(data.recordsFiltered || 0);
      setDraw(data.draw);
      setUseMockData(false);
    } catch (error) {
      console.error("Error loading contractors:", error);
      setUseMockData(true);
      setContractors(getMockContractors());
      setTotalRecords(getMockContractors().length);
      toast.warning("Using mock data due to backend connection issue");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, sortField, sortDirection, draw, isSearchFocused]);

  useEffect(() => {
    loadContractors();
  }, [loadContractors]);

  const getMockContractors = () => {
    return [
      {
        id: 1,
        name: "Accounts Team",
        email: "Michal.Ashton@global-guardians.co.uk",
        phone_number: "",
        web: "",
        date_review: "",
        status: "active",
      },
      {
        id: 2,
        name: "All Services 4 U Ltd",
        email: "Karl@allservices4u.co.uk",
        phone_number: "0203 603 1389",
        web: "https://allservices4u.co.uk/",
        date_review: "",
        status: "active",
      },
      {
        id: 3,
        name: "AMES Group Ltd",
        email: "terry@amesgroup.co.uk",
        phone_number: "0121 443 1111",
        web: "",
        date_review: "2020-09-18",
        status: "active",
      },
      {
        id: 4,
        name: "Asbestos365",
        email: "info@asbestos365.co.uk",
        phone_number: "",
        web: "www.asbestos365.co.uk",
        date_review: "2023-01-31",
        status: "active",
      },
      {
        id: 5,
        name: "BAM Construction",
        email: "",
        phone_number: "01442 238300",
        web: "http://www.bam.co.uk",
        date_review: "",
        status: "active",
      },
      {
        id: 6,
        name: "ABC Electrical Services",
        email: "info@abcelectrical.com",
        phone_number: "020 7123 4567",
        web: "www.abcelectrical.com",
        date_review: "2024-03-15",
        status: "active",
      },
      {
        id: 7,
        name: "London Gas Safety Ltd",
        email: "contact@londongas.com",
        phone_number: "020 7989 1234",
        web: "www.londongas.com",
        date_review: "2024-02-28",
        status: "active",
      },
      {
        id: 8,
        name: "Premier Plumbing Solutions",
        email: "hello@premierplumbing.co.uk",
        phone_number: "020 7456 7890",
        web: "www.premierplumbing.co.uk",
        date_review: "2024-01-20",
        status: "active",
      },
      {
        id: 9,
        name: "Safe Fire Systems",
        email: "info@safefire.co.uk",
        phone_number: "020 8234 5678",
        web: "www.safefire.co.uk",
        date_review: "2024-04-10",
        status: "active",
      },
      {
        id: 10,
        name: "Metro Heating & Cooling",
        email: "service@metroheating.com",
        phone_number: "020 7567 8901",
        web: "www.metroheating.com",
        date_review: "2024-03-05",
        status: "active",
      },
      {
        id: 11,
        name: "Elite Security Systems",
        email: "contact@elitesecurity.uk",
        phone_number: "020 7890 1234",
        web: "www.elitesecurity.uk",
        date_review: "2024-02-15",
        status: "active",
      },
      {
        id: 12,
        name: "Green Energy Solutions",
        email: "info@greenenergy.co.uk",
        phone_number: "020 8123 4567",
        web: "www.greenenergy.co.uk",
        date_review: "2024-01-30",
        status: "active",
      },
      {
        id: 13,
        name: "London Lift Services",
        email: "service@londonlift.com",
        phone_number: "020 8345 6789",
        web: "www.londonlift.com",
        date_review: "2024-04-05",
        status: "active",
      },
      {
        id: 14,
        name: "Premier Roofing Ltd",
        email: "hello@premierroofing.co.uk",
        phone_number: "020 8567 8901",
        web: "www.premierroofing.co.uk",
        date_review: "2024-03-20",
        status: "active",
      },
      {
        id: 15,
        name: "City Cleaning Services",
        email: "info@citycleaning.com",
        phone_number: "020 8789 0123",
        web: "www.citycleaning.com",
        date_review: "2024-02-10",
        status: "active",
      },
    ];
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    // Save cursor position before state update
    if (e.target.selectionStart !== null) {
      cursorPositionRef.current = e.target.selectionStart;
    }
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSearchFocus = (e) => {
    setIsSearchFocused(true);
    // Save cursor position on focus
    if (e.target.selectionStart !== null) {
      cursorPositionRef.current = e.target.selectionStart;
    }
  };

  const handleSearchBlur = () => {
    // Don't immediately set to false - delay to allow focus restoration
    setTimeout(() => {
      if (document.activeElement !== searchInputRef.current) {
        setIsSearchFocused(false);
      }
    }, 100);
  };

  const handleDeleteContractor = async (contractorId, contractorName) => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete "${contractorName}" and ALL related data:\n\n` +
      `• All contractor contacts\n` +
      `• All contractor services\n` +
      `• Unassign all tickets (tickets won't be deleted)\n` +
      `• The contractor record itself\n\n` +
      `This action cannot be undone. Are you sure you want to continue?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        if (!useMockData) {
          // Backend route: /api/contractors/{contractorId}
          const response = await api.delete(`/contractors/${contractorId}`);
          const result = response.data;
          console.log("Delete result:", result);
          
          if (result.success && result.data) {
            // Show detailed success message
            const deletedRecords = result.data.deleted_records || [];
            const totalDeleted = result.data.total_deleted || 0;
            toast.success(
              `"${contractorName}" deleted successfully! Removed: ${deletedRecords.join(', ')} (${totalDeleted} total records)`
            );
          } else {
            toast.success("Contractor deleted successfully");
          }
          loadContractors();
        } else {
          // Mock delete
          setContractors((prev) =>
            prev.filter((contractor) => contractor.id !== contractorId)
          );
          toast.success(`"${contractorName}" deleted successfully (Mock mode - all related records removed)`);
        }
      } catch (error) {
        console.error("Error deleting contractor:", error);
        toast.error(`Failed to delete contractor: ${error.message}`);
      }
    }
  };

  const handleExportCSV = () => {
    const csvContent = [
      ["Name", "Email", "Phone", "Web", "Review Date"],
      ...contractors.map((contractor) => [
        contractor.name,
        contractor.email || "",
        contractor.phone_number || "",
        contractor.web || "",
        contractor.date_review
          ? new Date(contractor.date_review).toLocaleDateString("en-GB")
          : "",
      ]),
    ]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contractors.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  // Don't show full-page loading if user is typing in search (to maintain focus)
  if (loading && !isSearchFocused) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading contractors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Contractors</span>
        </div>
      </div>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}
        <div className={styles.pageTitle}>
          <h1>Contractors</h1>
          <button
            className={styles.addButton}
            onClick={() => navigate("/contractors/create")}
          >
            <i className={styles.plusIcon}>+</i> Add Contractor
          </button>
        </div>

        {/* Table Controls */}
        <div className={styles.tableControls}>
          <div className={styles.entriesControl}>
            <label>
              Show{" "}
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                className={styles.entriesSelect}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
          </div>
          <div className={styles.tableSearch}>
            <label>
              Search:{" "}
              <input
                ref={searchInputRef}
                type="search"
                className={styles.searchInput}
                value={search}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder="Search contractors..."
                autoComplete="off"
              />
            </label>
          </div>
        </div>

        {/* Contractors Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.contractorsTable}>
            <thead>
              <tr>
                <th 
                  className="sortable"
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  E-mail<SortIcon direction={sortField === "email" ? sortDirection : null} isActive={sortField === "email"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("phone_number")}
                  style={{ cursor: "pointer" }}
                >
                  Phone<SortIcon direction={sortField === "phone_number" ? sortDirection : null} isActive={sortField === "phone_number"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("web")}
                  style={{ cursor: "pointer" }}
                >
                  Web<SortIcon direction={sortField === "web" ? sortDirection : null} isActive={sortField === "web"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("reviewDate")}
                  style={{ cursor: "pointer" }}
                >
                  Review Date<SortIcon direction={sortField === "reviewDate" ? sortDirection : null} isActive={sortField === "reviewDate"} />
                </th>
                <th className="text-center">PLI</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contractors.map((contractor) => (
                <tr key={contractor.id}>
                  <td>
                    <a
                      style={{
                        color: "#417ABA",
                        fontFamily: "Montserrat",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                      href={`/contractors/${contractor.id}`}
                      className={styles.nameLink}
                    >
                      {contractor.name}
                    </a>
                  </td>
                  <td>
                    {contractor.email ? (
                      <a
                        style={{
                          color: "#417ABA",
                          fontFamily: "Montserrat",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                        href={`mailto:${contractor.email}`}
                        className={styles.emailLink}
                      >
                        {contractor.email}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                  <td>{contractor.phone_number || ""}</td>
                  <td>
                    {contractor.web ? (
                      <a
                        style={{
                          color: "#417ABA",
                          fontFamily: "Montserrat",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                        href={
                          contractor.web.startsWith("http")
                            ? contractor.web
                            : `http://${contractor.web}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.webLink}
                      >
                        {contractor.web}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                  <td>{formatDate(contractor.date_review)}</td>
                  <td className="text-center">
                    {(() => {
                      const b = pliStatusBadge(contractor.pli_status);
                      return (
                        <span style={{ background: b.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                          {b.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={`${styles.actionButton} ${styles.showButton}`}
                      onClick={() => navigate(`/contractors/${contractor.id}`)}
                      title="Show"
                    >
                      Show
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.editButton}`}
                      onClick={() =>
                        navigate(`/contractors/${contractor.id}/edit`)
                      }
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.removeButton}`}
                      onClick={() =>
                        handleDeleteContractor(contractor.id, contractor.name)
                      }
                      title="Remove"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalRecords > 0 && (
          <div className={styles.paginationWrapper}>
            <div className={styles.paginationInfo}>
              Showing {startIndex + 1} to {endIndex} of {totalRecords} entries
            </div>
            <div className={styles.pagination}>
              <button
                className={`${styles.paginationButton} ${
                  currentPage === 1 ? styles.disabled : ""
                }`}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                className={`${styles.paginationButton} ${
                  currentPage === 1 ? styles.disabled : ""
                }`}
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = i + 1;
                // Show pages around current page
                if (totalPages > 5) {
                  if (currentPage > 3) {
                    pageNum = currentPage - 2 + i;
                  }
                  if (pageNum > totalPages) {
                    pageNum = totalPages - 4 + i;
                  }
                }
                return (
                  <button
                    key={pageNum}
                    className={`${styles.paginationButton} ${
                      currentPage === pageNum ? styles.active : ""
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className={`${styles.paginationButton} ${
                  currentPage === totalPages ? styles.disabled : ""
                }`}
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button
                className={`${styles.paginationButton} ${
                  currentPage === totalPages ? styles.disabled : ""
                }`}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          </div>
        )}

        {contractors.length === 0 && !loading && (
          <div className={styles.noData}>
            <p>No contractors found!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contractors;
